/* eslint-disable no-underscore-dangle */
/**
 * Stable identifier for an order_line within a booking. Mirrors the
 * server-side getOrderLineKey at
 *   pickuphungary-dashboard-api/src/packages/ph-core/src/Helpers/OrderLineKey.helper.ts
 *
 * The two files MUST stay in lockstep — backend validates incoming
 * order_line_key against keys computed by this exact algorithm.
 *
 * `index` is a fallback only — used when metadata is missing for a kind
 * that should have it (defensive against pre-migration or malformed data).
 */
export function getOrderLineKey(line, index) {
  const kind = line.kind || 'unknown';
  const metadata = line.metadata || {};

  switch (kind) {
    case 'adjustment': {
      const id = line._id ? String(line._id) : null;
      return id ? `adjustment:${id}` : `adjustment:idx-${index}`;
    }
    case 'base':
      return 'base';
    case 'service': {
      const { service_id: serviceId } = metadata;
      return serviceId ? `service:${serviceId}` : `service:idx-${index}`;
    }
    case 'operator': {
      const { operator_id: opId, role } = metadata;
      if (opId && role) return `operator:${opId}:${role}`;
      if (opId) return `operator:${opId}`;
      return `operator:idx-${index}`;
    }
    case 'partner_cancellation':
      return 'partner_cancellation';
    case 'operator_cancellation': {
      const opId = metadata.operator_id;
      return opId ? `operator_cancellation:${opId}` : 'operator_cancellation';
    }
    case 'cash_from_client': {
      const opId = metadata.operator_id;
      return opId ? `cash_from_client:${opId}` : 'cash_from_client';
    }
    default:
      return `${kind}:idx-${index}`;
  }
}

/**
 * Every order_line_key a booking can legitimately carry. Mirrors
 * `getOrderLineKeySetForBooking` in the same API helper as above, and must
 * stay in step with it for the same reason: the backend validates an
 * incoming `order_line_key` against exactly this set.
 *
 * Read it as "which keys are LIVE", which is what makes a key on a payment
 * recoverable rather than lost — see `orderLinePaid.js` rule 4. It is the
 * FULL set, every kind: handing a reader only the customer-facing keys would
 * reclassify an `operator:<id>` key as booking-level money and let an
 * operator payout settle a customer's line.
 *
 * The synthetic branch is not a nicety. A pre-OrderLinesBuilder booking has
 * no `order_lines[]` at all, and the backend accepts (and
 * `computeAllocatableLines` renders) a `base` key derived from
 * `total_price`; without the same fallback here, every key on such a
 * booking's payments would read as dead.
 */
export function getOrderLineKeySetForBooking(booking) {
  const orderLines = Array.isArray(booking?.order_lines) ? booking.order_lines : [];
  const real = new Set(orderLines.map((line, i) => getOrderLineKey(line, i)));
  if (real.size > 0) return real;

  const synth = new Set();
  const isCancelled = booking?.status === 'Cancelled';
  const partnerOwed = Number(
    isCancelled ? (booking?.partner_cancellation_price ?? 0) : (booking?.total_price ?? 0),
  );
  if (partnerOwed > 0) synth.add('base');

  const operatorIds = new Set();
  if (typeof booking?.operator_id === 'string' && booking.operator_id) {
    operatorIds.add(booking.operator_id);
  }
  (booking?.operators ?? []).forEach((op) => {
    if (op?.operator_id) operatorIds.add(op.operator_id);
  });
  const operatorOwed = Number(
    isCancelled ? (booking?.operator_cancellation_price ?? 0) : (booking?.operator_price ?? 0),
  );
  if (operatorOwed > 0) {
    operatorIds.forEach((id) => {
      synth.add(isCancelled ? `operator_cancellation:${id}` : `operator:${id}`);
    });
  }
  return synth;
}

export default getOrderLineKey;
